import {
  checkNavigationBlockers,
  checkParcelNavigationBlockers,
  isPathWithParcelNavigationBlocker,
  isUnifiedBlockerEnabled,
} from 'root/apps/utils/navigationBlocker';

import history from './history';

const FREE_TIER_MODULE = 'customers.invoicing_integration.free_tier';

// In most cases Retail navigation happens at the domain root level '/'.
// It also happens that retail is reading controllers from inside folders but
// the paths always ends by .php extension (/oauth/remove_openid.php).
// If these conditions are not met, router should be initializing a React page.
function isReactPagePath() {
  return window.location.pathname !== '/' && !~window.location.pathname.indexOf('.php');
}

window.isReactPage = isReactPagePath();

// Page router class is how React page is mounted. It hides all of Retail
// gui_def containers and sends a mounting event `/pages/index.js` to mount
// the Reaqct app. Use `PageRouter.goTo('/inventory/pagex')`.
export default class PageRouter implements RetailPageRouter {
  goTo(url: string, historyHandler: string = 'push') {
    const runNavigation = () => {
      this.mount(url, historyHandler);
      window.history.clearLeaveStateStack();
    };

    if (isUnifiedBlockerEnabled()) {
      const skip = window.navigationBlockersSkipNextCheck === true;
      window.navigationBlockersSkipNextCheck = false;
      if (skip) {
        runNavigation();
        return;
      }
      checkNavigationBlockers()
        .then(runNavigation)
        .catch(() => {
          // Save failed; the registered blocker surfaces its own error UI. Navigation stays on current page.
        });
    } else {
      // LEGACY (FF off): parcel-only path. Remove this block when ui.navigation_blocker.unified is fully on.
      const hasFreeTierModule = window.lsCloud.Modules.hasModule(FREE_TIER_MODULE);
      const skipNextCheck = window.parcelNavigationBlockersSkipNextCheck === true;
      const currentReactPath = history.location?.pathname ?? window.location.pathname;
      const pathMatches = isPathWithParcelNavigationBlocker(currentReactPath);
      const shouldCheckParcelBlockers = hasFreeTierModule && pathMatches && !skipNextCheck;
      if (shouldCheckParcelBlockers) {
        checkParcelNavigationBlockers()
          .then(runNavigation)
          .catch(() => {
            // Save failed; MFE is notified via onSaveError and shows its own toast. Navigation stays on current page.
          });
      } else {
        window.parcelNavigationBlockersSkipNextCheck = false;
        runNavigation();
      }
    }
  }

  /**
   * Don't call this method directly, use `goTo`.
   * It sends a mount event to the Page component
   * @param {String} url reactRouter url to mount
   * @param {String} historyHandler 'push' or 'replace'
   * if mounted manually, will be 'push', if through browser popstate 'replace'
   */
  mount(url: string, historyHandler: string) {
    // Handle PUSH and REPLACE history to be in sync for react-router
    if (historyHandler) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      history[historyHandler](url);
    }

    // LEGACY (FF off): track the current React route for back/forward parcel blocker checks.
    // Must be before the isReactPage early return so React-to-React navigation keeps it current.
    if (!isUnifiedBlockerEnabled()) {
      window.parcelNavigationBlockersCurrentPath = url;
    }

    // If current location is a react page, and next or previous is also a react page
    // This prevents re-mounting and only writes in the history
    if (window.isReactPage) return;

    window.isReactPage = true;

    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
    window.merchantos.page.hide();

    // Timeout added to mount after history change.
    setTimeout(() => {
      window.merchantos.asyncSubscribe.notifySubscribers('react.mount.page', {
        props: {
          url,
        },
      });
    });
  }

  unmount() {
    if (!window.isReactPage) return;
    window.isReactPage = false;

    window.merchantos.asyncSubscribe.notifySubscribers('react.unmount.page');
    // LEGACY (FF off): clear the parcel blocker path tracker.
    if (!isUnifiedBlockerEnabled()) {
      window.parcelNavigationBlockersCurrentPath = undefined;
    }
  }
}

window.PageRouter = new PageRouter();

// window.onpopstate is preserved so the existing behaviours still works.
// The declaration here instead of inside router.js is to keep all of the
// `mount / unmount` code at the same place.
const routerOnpopstate = window.onpopstate;

function handlePopstateNavigation(event: TSFixMe) {
  const targetLocation = event?.target?.location;
  const targetPathname = targetLocation?.pathname;
  const targetSearch = targetLocation?.search ?? '';
  const targetPath = `${targetPathname || ''}${targetSearch}`;
  // Use the same heuristic as isReactPagePath: React routes have a non-root pathname without '.php'.
  const isReactTargetPath =
    !!targetPathname && targetPathname !== '/' && !~targetPathname.indexOf('.php');

  // Handle the history `back / previous` to `mount / unmount` the react page
  if (isReactTargetPath) {
    window.PageRouter.mount(targetPath, 'replace');
    return;
  }

  window.PageRouter.unmount();
  if (typeof routerOnpopstate === 'function') {
    routerOnpopstate(event);
  }
}

window.onpopstate = (event: TSFixMe) => {
  // Legacy hack: when views.ts sets window._ignore_popstate, we must bypass the React router
  // and delegate directly to the original routerOnpopstate so legacy go_back flows keep working.
  /* eslint-disable no-underscore-dangle */
  if (window._ignore_popstate) {
    if (typeof routerOnpopstate === 'function') {
      routerOnpopstate(event);
    }
    return;
  }

  if (isUnifiedBlockerEnabled()) {
    checkNavigationBlockers()
      .then(() => {
        handlePopstateNavigation(event);
      })
      .catch(() => {
        // Save failed; the registered blocker surfaces its own error UI. Stay on current page.
      });
  } else {
    // LEGACY (FF off): parcel-only path. Remove when ui.navigation_blocker.unified is fully on.
    const hasFreeTierModule = window.lsCloud.Modules.hasModule(FREE_TIER_MODULE);
    const leavingPath =
      window.parcelNavigationBlockersCurrentPath ||
      history.location?.pathname ||
      window.location.pathname;
    const leavingMatches = leavingPath && isPathWithParcelNavigationBlocker(leavingPath);
    const shouldCheckParcelBlockers = hasFreeTierModule && !!leavingMatches;

    if (shouldCheckParcelBlockers) {
      checkParcelNavigationBlockers()
        .then(() => {
          handlePopstateNavigation(event);
        })
        .catch(() => {
          // Save failed; MFE is notified via onSaveError and shows its own toast. Stay on current page.
        });
    } else {
      handlePopstateNavigation(event);
    }
  }
};
